import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiX } from 'react-icons/fi';

const SOURCE_CODES_BY_SUPPLIER = {
    Bean: ['BeanSup1', 'BeanSup2', 'BeanSup3'],
    Doraemon: ['DoraemonSupB', 'DoraemonSupCD']
};

const ALL_SOURCE_CODES = [
    'DoraemonSupB',
    'DoraemonSupCD',
    'BeanSup1',
    'BeanSup2',
    'BeanSup3'
];

const SOURCE_TO_SUPPLIER = Object.entries(SOURCE_CODES_BY_SUPPLIER).reduce((acc, [supplier, sourceCodes]) => {
    sourceCodes.forEach((sourceCode) => {
        acc[sourceCode] = supplier;
    });
    return acc;
}, {});

const createEmptyCostTableRow = () => ({
    effectiveFrom: '',
    DoraemonSupB: '',
    DoraemonSupCD: '',
    BeanSup1: '',
    BeanSup2: '',
    BeanSup3: ''
});

const buildCostTableRows = (supplierSources = []) => {
    const rowMap = new Map();

    supplierSources.forEach((source) => {
        const sourceCode = source?.sourceCode;
        if (!sourceCode || !ALL_SOURCE_CODES.includes(sourceCode)) return;

        (source.costHistory || []).forEach((row) => {
            const effectiveFrom = row?.effectiveFrom
                ? new Date(row.effectiveFrom).toISOString().split('T')[0]
                : '';
            if (!effectiveFrom) return;

            if (!rowMap.has(effectiveFrom)) {
                rowMap.set(effectiveFrom, createEmptyCostTableRow());
                rowMap.get(effectiveFrom).effectiveFrom = effectiveFrom;
            }

            rowMap.get(effectiveFrom)[sourceCode] = row.cost != null ? String(row.cost) : '';
        });
    });

    const rows = [...rowMap.values()].sort((a, b) => new Date(a.effectiveFrom) - new Date(b.effectiveFrom));
    return rows.length ? rows : [createEmptyCostTableRow()];
};

const buildSupplierSourcesFromTable = (rows = []) => {
    return ALL_SOURCE_CODES.map((sourceCode) => ({
        supplier: SOURCE_TO_SUPPLIER[sourceCode],
        sourceCode,
        costHistory: rows
            .map((row) => ({
                cost: row[sourceCode] !== '' ? parseFloat(row[sourceCode]) : undefined,
                effectiveFrom: row.effectiveFrom || undefined
            }))
            .filter((row) => row.effectiveFrom && typeof row.cost === 'number' && !Number.isNaN(row.cost))
    })).filter((source) => source.costHistory.length > 0);
};

const createEmptyUnitPriceHistoryRow = () => ({ effectiveFrom: '', unitPrice: '' });

const buildUnitPriceHistoryRows = (unitPriceHistory = []) => {
    const rows = [...(unitPriceHistory || [])]
        .filter((r) => r?.effectiveFrom)
        .sort((a, b) => new Date(a.effectiveFrom) - new Date(b.effectiveFrom))
        .map((r) => ({
            effectiveFrom: new Date(r.effectiveFrom).toISOString().split('T')[0],
            unitPrice: r.unitPrice != null ? String(r.unitPrice) : ''
        }));
    return rows.length ? rows : [createEmptyUnitPriceHistoryRow()];
};

const SimonProductModal = ({ mode = 'create', product = null, categories = [], onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        category: categories[0] || 'Pods',
        productName: '',
        unitPrice: '',
        unitPriceHistoryRows: [createEmptyUnitPriceHistoryRow()],
        costTableRows: [createEmptyCostTableRow()],
        remarks: '',
        isActive: true
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (mode === 'edit' && product) {
            setFormData({
                category: product.category || categories[0] || 'Pods',
                productName: product.productName || '',
                unitPrice: product.unitPrice != null ? String(product.unitPrice) : '',
                unitPriceHistoryRows: buildUnitPriceHistoryRows(product.unitPriceHistory || []),
                costTableRows: buildCostTableRows(product.supplierSources || []),
                remarks: product.remarks || '',
                isActive: product.isActive !== undefined ? Boolean(product.isActive) : true
            });
        }
    }, [mode, product, categories]);

    const updateUnitPriceHistoryRow = (rowIndex, field, value) => {
        const next = [...formData.unitPriceHistoryRows];
        next[rowIndex] = { ...(next[rowIndex] || createEmptyUnitPriceHistoryRow()), [field]: value };
        setFormData((prev) => ({ ...prev, unitPriceHistoryRows: next }));
    };

    const addUnitPriceHistoryRow = () => {
        setFormData((prev) => ({
            ...prev,
            unitPriceHistoryRows: [...prev.unitPriceHistoryRows, createEmptyUnitPriceHistoryRow()]
        }));
    };

    const removeUnitPriceHistoryRow = (rowIndex) => {
        const next = formData.unitPriceHistoryRows.filter((_, index) => index !== rowIndex);
        setFormData((prev) => ({
            ...prev,
            unitPriceHistoryRows: next.length ? next : [createEmptyUnitPriceHistoryRow()]
        }));
    };

    const updateCostTableRow = (rowIndex, field, value) => {
        const next = [...formData.costTableRows];
        next[rowIndex] = { ...(next[rowIndex] || createEmptyCostTableRow()), [field]: value };
        setFormData((prev) => ({ ...prev, costTableRows: next }));
    };

    const addCostTableRow = () => {
        setFormData((prev) => ({ ...prev, costTableRows: [...prev.costTableRows, createEmptyCostTableRow()] }));
    };

    const removeCostTableRow = (rowIndex) => {
        const next = formData.costTableRows.filter((_, index) => index !== rowIndex);
        setFormData((prev) => ({ ...prev, costTableRows: next.length ? next : [createEmptyCostTableRow()] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const unitPriceHistory = formData.unitPriceHistoryRows
                .map((row) => ({
                    unitPrice: row.unitPrice !== '' ? parseFloat(row.unitPrice) : undefined,
                    effectiveFrom: row.effectiveFrom || undefined
                }))
                .filter(
                    (row) =>
                        row.effectiveFrom &&
                        typeof row.unitPrice === 'number' &&
                        !Number.isNaN(row.unitPrice)
                );

            const payload = {
                category: formData.category,
                productName: formData.productName.trim(),
                unitPrice: formData.unitPrice !== '' ? parseFloat(formData.unitPrice) : undefined,
                unitPriceHistory,
                remarks: formData.remarks.trim(),
                isActive: formData.isActive,
                supplierSources: buildSupplierSourcesFromTable(formData.costTableRows)
            };

            const token = localStorage.getItem('token');
            const request = mode === 'edit'
                ? axios.put(`${process.env.REACT_APP_API_URL}/api/simonproducts/${product._id}`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                })
                : axios.post(`${process.env.REACT_APP_API_URL}/api/simonproducts`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                });

            const response = await request;
            if (response.data.success) {
                onSuccess(response.data.data);
            }
        } catch (err) {
            setError(`Failed to ${mode} Simon product: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-6xl my-8 min-w-0 overflow-x-hidden">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{mode === 'edit' ? 'Edit Simon Product' : 'Create Simon Product'}</h2>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" aria-label="Close modal">
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 max-h-[72vh] overflow-y-auto pr-1">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                                <select
                                    required
                                    value={formData.category}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    {(categories.length ? categories : ['Pods', 'Device', 'Lana', 'Disposable', 'Heet']).map((category) => (
                                        <option key={category} value={category}>{category}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.productName}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, productName: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.unitPrice}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, unitPrice: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="Optional"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    If you add dated rows below, the server sets unit price from the latest effective date.
                                </p>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Unit Price History</label>
                            <p className="text-xs text-gray-500 mb-3">Optional: one row per date when the retail unit price changes.</p>
                            <div className="overflow-x-auto border border-gray-200 rounded-lg max-w-xl">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700">Date</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700">Unit Price</th>
                                            <th className="px-3 py-2 text-right font-medium text-gray-700">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {formData.unitPriceHistoryRows.map((row, rowIndex) => (
                                            <tr key={`unit-price-row-${rowIndex}`}>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="date"
                                                        value={row.effectiveFrom}
                                                        onChange={(e) => updateUnitPriceHistoryRow(rowIndex, 'effectiveFrom', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded-md"
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={row.unitPrice}
                                                        onChange={(e) => updateUnitPriceHistoryRow(rowIndex, 'unitPrice', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded-md"
                                                        placeholder="Price"
                                                    />
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    {formData.unitPriceHistoryRows.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeUnitPriceHistoryRow(rowIndex)}
                                                            className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <button
                                type="button"
                                onClick={addUnitPriceHistoryRow}
                                className="mt-3 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                            >
                                + Add Unit Price Date Row
                            </button>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Supplier Source & Cost History Table</label>
                            <p className="text-xs text-gray-500 mb-3">
                                Add one row per effective date. Fill only the supplier source columns that change on that date.
                            </p>
                            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700">Date</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700">DoraemonSupB</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700">DoraemonSupCD</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700">BeanSup1</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700">BeanSup2</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700">BeanSup3</th>
                                            <th className="px-3 py-2 text-right font-medium text-gray-700">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {formData.costTableRows.map((row, rowIndex) => (
                                            <tr key={`cost-table-row-${rowIndex}`}>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="date"
                                                        value={row.effectiveFrom}
                                                        onChange={(e) => updateCostTableRow(rowIndex, 'effectiveFrom', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded-md"
                                                    />
                                                </td>
                                                {ALL_SOURCE_CODES.map((sourceCode) => (
                                                    <td key={`${sourceCode}-${rowIndex}`} className="px-3 py-2">
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={row[sourceCode] || ''}
                                                            onChange={(e) => updateCostTableRow(rowIndex, sourceCode, e.target.value)}
                                                            className="w-full px-2 py-1 border border-gray-300 rounded-md"
                                                            placeholder="Cost"
                                                        />
                                                    </td>
                                                ))}
                                                <td className="px-3 py-2 text-right">
                                                    {formData.costTableRows.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeCostTableRow(rowIndex)}
                                                            className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <button
                                type="button"
                                onClick={addCostTableRow}
                                className="mt-3 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                            >
                                + Add Date Row
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                                <textarea
                                    rows="3"
                                    value={formData.remarks}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, remarks: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div className="flex items-center pt-8">
                                <label className="inline-flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                                    />
                                    <span className="text-sm font-medium text-gray-700">Active</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            disabled={loading}
                        >
                            {loading ? (mode === 'edit' ? 'Updating...' : 'Creating...') : (mode === 'edit' ? 'Update' : 'Create')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SimonProductModal;
